// src/api/sidebar-queries.ts
import { getAllUsers } from "@/utils/allUsersApi";
import { getAlllVendor } from "@/utils/vendorApi";
import {
  create_or_get_chat,
  getUserChats,
  getUnreadChatCount,
  markChatAsRead,
} from "@/utils/vendorChatApi";
import {
  getVendorReviews,
  replyToReview,
  sendPrivateReviewMessage,
} from "@/utils/vendorApi";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import type { RootState } from "@/redux/store";
import { record, vendorMarketplaceKeys as keys } from "@/components/VendorInfo/chat/vendorMessageState";
import type { MarketplaceConversation } from "@/utils/conversationBoundary";

export const useVendors = () => {
  return useQuery({
    queryKey: ["vendors"],
    queryFn: async () => {
      try {
        const data = await getAlllVendor();
        // Ensure we always return an array
        return Array.isArray(data) ? data : [];
      } catch (error) {
        console.error("Error fetching vendors:", error);
        return []; // Return empty array on error
      }
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useUsers = (enabled = true) => {
  return useQuery({
    queryKey: ["users"],
    queryFn: async () => {
      const data: unknown = await getAllUsers();
      if (!Array.isArray(data)) throw new Error("Customers could not be loaded.");
      return data as unknown[];
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled,
  });
};

export const useCreateOrGetChat = () => {
  const queryClient = useQueryClient();
  const accountId = useSelector((state: RootState) => state.vendor.vendor?._id || "");
  return useMutation({
    mutationFn: ({ receiverId, token }: { receiverId: string; token: string | null }) =>
      create_or_get_chat({ receiverId }, token),
    onSuccess: (chat) => {
      queryClient.setQueryData<MarketplaceConversation[]>(keys.chats(accountId), (old) => [...(old || []).filter((item) => item._id !== chat._id), chat]);
      queryClient.invalidateQueries({ queryKey: keys.chats(accountId) });
    },
  });
};

export const useSearchContacts = (searchQuery: string, enabled = true) => {
  const { data: users = [], isLoading: usersLoading, error } = useUsers(enabled);

  const filteredUsers = users.filter((user) => {
    const customer = record(user);
    return typeof customer?.name === "string" && customer.name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  return {
    data: filteredUsers,
    isLoading: usersLoading,
    error,
  };
};

export const useUserChats = (token: string | null) => {
  const accountId = useSelector((state: RootState) => state.vendor.vendor?._id || "");
  return useQuery({
    queryKey: keys.chats(accountId),
    queryFn: () => getUserChats(token),
    enabled: !!token,
    staleTime: 1000 * 60 * 2, // 2 min – tune to taste
    retry: 1,
  });
};

// Marketplace unread state never shares the Customer Care state or cache.
export const useUnreadChatCount = (userId: string | null | undefined) => {
  const token = useSelector((state: RootState) => state.vendor.token);
  return useQuery({ queryKey: keys.unread(userId || ""), queryFn: () => getUnreadChatCount(userId || ""), enabled: !!userId && !!token, refetchInterval: 15000, staleTime: 10000, retry: 1 });
};
export const useMarkChatAsRead = () => {
  const queryClient = useQueryClient();
  return useMutation({ mutationFn: ({chatId,userId}: {chatId:string;userId:string}) => markChatAsRead(chatId,userId), onSuccess: (_data,variables) => {
    queryClient.setQueryData<{count:number;chats:Record<string,number>}>(keys.unread(variables.userId),(previous) => {
      const chats = {...previous?.chats,[variables.chatId]:0};
      return {count:Object.values(chats).reduce((sum,value)=>sum+value,0),chats};
    });
    void queryClient.invalidateQueries({queryKey:keys.unread(variables.userId)});
  }});
};

// Hook for fetching vendor reviews (with optional paging and status filter)
export const useVendorReviews = (params: { token?: string | null }) => {
  const { token } = params || {};
  return useQuery({
    queryKey: ["vendorReviews", { token }],
    queryFn: () => getVendorReviews(token ?? null),
    enabled: !!token,
    staleTime: 1000 * 60 * 3, // 3 minutes
    retry: 1,
  });
};

// Mutation: reply publicly or privately to a review
export const useReplyToReview = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      reviewId,
      payload,
      token,
    }: {
      reviewId: string;
      payload: { message: string; isPublic?: boolean; reviewId?: string };
      token?: string | null;
    }) => {
      if (!payload?.message || !payload.message.trim())
        throw new Error("Reply message is required");
      return replyToReview(reviewId, payload, token ?? null);
    },
    // Optimistic update: update all vendorReviews queries so UI updates regardless of key shape
    onMutate: async (variables) => {
      const { reviewId, payload } = variables as any;

      // find all queries that start with ['vendorReviews']
      const queries = queryClient.getQueriesData({
        queryKey: ["vendorReviews"],
      });
      await queryClient.cancelQueries({ queryKey: ["vendorReviews"] });

      const previous: Array<any> = queries.map(([k, data]: any) => [k, data]);

      try {
        for (const [key] of queries) {
          queryClient.setQueryData(key, (old: any) => {
            if (!old) return old;
            const cloned = { ...old };
            const reviews = Array.isArray(cloned.reviews) ? cloned.reviews : [];
            cloned.reviews = reviews.map((r: any) => {
              const idMatches = String(r._id ?? r.id) === String(reviewId);
              if (!idMatches) return r;
              return {
                ...r,
                vendorReply: {
                  message: payload.message.trim(),
                  repliedAt: new Date().toISOString(),
                  repliedBy: "you",
                  isPublic: payload.isPublic !== false,
                },
              };
            });
            return cloned;
          });
        }
      } catch (e) {
        // ignore optimistic update failures
      }

      return { previous };
    },
    onError: (error: any, _variables, context: any) => {
      const prev = context?.previous as Array<any> | undefined;
      if (prev) {
        for (const [key, data] of prev) {
          queryClient.setQueryData(key, data);
        }
      }
      console.error("Failed to reply review:", error);
      toast.error(error.message || "Failed to reply review");
    },
    onSuccess: (_data, _variables) => {
      // invalidate vendor reviews to refetch authoritative data
      queryClient.invalidateQueries({ queryKey: ["vendorReviews"] });
      toast.success("Replied to review successfully", {
        position: "top-right",
        autoClose: 3000,
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["vendorReviews"] });
    },
  });
};

// Mutation: send a private message related to a review (vendor <-> customer)
export const useSendPrivateReviewMessage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      token,
      body,
    }: {
      token?: string | null;
      body: { reviewId: string; message: string; messageType?: string };
    }) => sendPrivateReviewMessage(token ?? null, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vendorReviews"] });
      toast.success("Private message sent", {
        position: "top-right",
        autoClose: 3000,
      });
    },
    onError: (error: any) => {
      console.error("Failed to send private message:", error);
      toast.error(error.message || "Failed to send message");
    },
  });
};
